import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import {
  Inbox,
  Upload,
  FileText,
  Clock,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Trash2,
  Link as LinkIcon,
  FileSpreadsheet,
  Sparkles,
  User,
  Calendar,
  Euro,
  Building2,
  Archive,
  Loader2,
  ChevronRight,
  Plus,
  Filter,
  Zap,
  TrendingUp,
  Receipt,
  FileCheck,
  AlertTriangle,
  Eye,
  ArrowRight,
  ArrowLeft,
  Star
} from 'lucide-react';
import { inboxAPI, clientsAPI } from '../../services/api';
import { useNotifications } from '../../contexts/NotificationContext';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// Animation CSS
const animationStyles = `
  @keyframes slideIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse-soft {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  @keyframes success-pop {
    0% { transform: scale(0.8); opacity: 0; }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .animate-slide-in { animation: slideIn 0.3s ease-out forwards; }
  .animate-pulse-soft { animation: pulse-soft 2s ease-in-out infinite; }
  .animate-success-pop { animation: success-pop 0.4s ease-out forwards; }
  .shimmer-bg {
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }
`;

const InboxPage = () => {
  const navigate = useNavigate();
  const { addLocalNotification } = useNotifications();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, processed: 0, error: 0, this_month: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showLinkClientModal, setShowLinkClientModal] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [filter, setFilter] = useState('all');
  const [processing, setProcessing] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, item: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsRes, statsRes] = await Promise.all([
        inboxAPI.getAll(filter === 'all' ? null : filter),
        inboxAPI.getStats()
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load inbox:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await clientsAPI.getAll();
      setClients(res.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    let successCount = 0;
    const total = acceptedFiles.length;

    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subject', file.name);

      try {
        await inboxAPI.upload(formData);
        successCount++;
        setUploadProgress(((i + 1) / total) * 100);
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`Erreur: ${file.name}`);
      }
    }

    if (successCount > 0) {
      setShowSuccessAnimation(true);
      setTimeout(() => setShowSuccessAnimation(false), 2000);
      
      toast.success(
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <span>{successCount} document(s) envoyé(s) à l'IA</span>
        </div>
      );
      
      // Notification in-app
      addLocalNotification({
        type: 'inbox',
        title: 'Documents importés',
        message: `${successCount} document(s) en cours d'analyse par l'IA`,
        link: '/dashboard/smartfacture/inbox'
      });
      
      loadData();
    }
    
    setUploading(false);
    setUploadProgress(0);
  }, [addLocalNotification]);

  const { getRootProps, getInputProps, isDragActive, isDragAccept } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10 * 1024 * 1024
  });

  const handleReprocess = async (itemId) => {
    setProcessing(prev => ({ ...prev, [itemId]: true }));
    try {
      await inboxAPI.reprocess(itemId);
      toast.success('Nouvelle analyse lancée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors du retraitement');
    } finally {
      setProcessing(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleDelete = async (item) => {
    setDeleteConfirm({ open: true, item });
  };

  const confirmDelete = async () => {
    const item = deleteConfirm.item;
    if (!item) return;
    
    setDeleting(true);
    try {
      await inboxAPI.delete(item.id);
      toast.success('Document supprimé');
      loadData();
      if (selectedItem?.id === item.id) {
        setShowDetailModal(false);
        setSelectedItem(null);
      }
      setDeleteConfirm({ open: false, item: null });
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleArchive = async (itemId) => {
    try {
      await inboxAPI.archive(itemId);
      toast.success('Document archivé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleLinkClient = async () => {
    if (!selectedClientId || !selectedItem) return;
    try {
      await inboxAPI.linkClient(selectedItem.id, selectedClientId);
      toast.success('Client associé');
      setShowLinkClientModal(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'association');
    }
  };

  const handleCreateInvoice = async (itemId, clientId = null) => {
    setProcessing(prev => ({ ...prev, [`invoice_${itemId}`]: true }));
    try {
      const res = await inboxAPI.createInvoice(itemId, clientId);
      
      // Animation de succès
      setShowSuccessAnimation(true);
      setTimeout(() => setShowSuccessAnimation(false), 2000);
      
      toast.success(
        <div className="flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-emerald-500" />
          <span>Facture {res.data.invoice_number} créée !</span>
        </div>
      );
      
      // Notification in-app
      addLocalNotification({
        type: 'success',
        title: 'Facture créée',
        message: `La facture ${res.data.invoice_number} a été créée depuis votre Inbox`,
        link: '/dashboard/smartfacture'
      });
      
      loadData();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setProcessing(prev => ({ ...prev, [`invoice_${itemId}`]: false }));
    }
  };

  const openDetailModal = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const openLinkClientModal = (item) => {
    setSelectedItem(item);
    loadClients();
    setSelectedClientId(item.linked_client_id || '');
    setShowLinkClientModal(true);
  };

  // États clairs pour les utilisateurs
  const getStatusConfig = (item) => {
    if (item.linked_invoice_id) {
      return {
        label: 'Facture créée',
        icon: FileCheck,
        bgClass: 'bg-emerald-500',
        textClass: 'text-white',
        description: `Facture ${item.linked_invoice_number}`,
        color: 'emerald'
      };
    }
    switch (item.status) {
      case 'processed':
        return {
          label: 'Prêt',
          icon: CheckCircle2,
          bgClass: 'bg-blue-500',
          textClass: 'text-white',
          description: 'Données extraites, prêt à facturer',
          color: 'blue'
        };
      case 'processing':
        return {
          label: 'Analyse...',
          icon: Loader2,
          bgClass: 'bg-indigo-500',
          textClass: 'text-white',
          description: 'L\'IA analyse votre document',
          color: 'indigo',
          animate: true
        };
      case 'pending':
        return {
          label: 'À traiter',
          icon: Clock,
          bgClass: 'bg-amber-500',
          textClass: 'text-white',
          description: 'En attente d\'analyse',
          color: 'amber'
        };
      case 'error':
        return {
          label: 'À vérifier',
          icon: AlertTriangle,
          bgClass: 'bg-red-500',
          textClass: 'text-white',
          description: 'Relancez l\'analyse ou vérifiez le document',
          color: 'red'
        };
      case 'archived':
        return {
          label: 'Archivé',
          icon: Archive,
          bgClass: 'bg-slate-400',
          textClass: 'text-white',
          description: 'Document archivé',
          color: 'slate'
        };
      default:
        return {
          label: item.status,
          icon: FileText,
          bgClass: 'bg-slate-500',
          textClass: 'text-white',
          description: '',
          color: 'slate'
        };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return "Aujourd'hui";
    if (days === 1) return "Hier";
    if (days < 7) return `Il y a ${days} jours`;
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short'
    });
  };

  const formatAmount = (amount, currency = 'EUR') => {
    if (!amount && amount !== 0) return null;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getDocTypeLabel = (docType) => {
    const types = {
      'facture': 'Facture fournisseur',
      'devis': 'Devis',
      'ticket': 'Ticket de caisse',
      'avoir': 'Avoir',
      'autre': 'Document'
    };
    return types[docType] || 'Document';
  };

  return (
    <>
      <style>{animationStyles}</style>
      
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard/smartfacture')}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        {/* Header Premium */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-blue-600 to-violet-600 p-6 md:p-8 text-white">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzLTItMi00LTJoLTR2MTBoNGMyIDAgNC0yIDQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
          
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm">
                <Inbox className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">
                  Inbox SmartFacture
                </h1>
                <p className="text-blue-100 mt-1 text-sm md:text-base">
                  Importez vos documents — l'IA fait le reste
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="secondary" 
                onClick={loadData} 
                disabled={loading}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </div>
          
          {/* Quick Stats inline */}
          <div className="relative mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'À traiter', value: stats.pending, icon: Clock, color: 'amber' },
              { label: 'Prêts', value: stats.processed, icon: CheckCircle2, color: 'emerald' },
              { label: 'Erreurs', value: stats.error, icon: AlertTriangle, color: 'red' },
              { label: 'Ce mois', value: stats.this_month, icon: Calendar, color: 'blue' }
            ].map((stat, idx) => (
              <div 
                key={idx}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4"
              >
                <div className="flex items-center gap-2">
                  <stat.icon className={`h-4 w-4 text-${stat.color}-300`} />
                  <span className="text-xs text-blue-100">{stat.label}</span>
                </div>
                <p className="text-2xl md:text-3xl font-bold mt-1">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Upload Zone Premium */}
        <div
          {...getRootProps()}
          className={`
            relative overflow-hidden rounded-2xl border-2 border-dashed transition-all duration-300 cursor-pointer
            ${isDragActive 
              ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
              : 'border-slate-300 bg-gradient-to-br from-slate-50 to-white hover:border-blue-400 hover:bg-blue-50/50'
            }
            ${uploading ? 'pointer-events-none' : ''}
          `}
        >
          <input {...getInputProps()} data-testid="inbox-file-input" />
          
          <div className="p-8 md:p-12 text-center">
            {uploading ? (
              <div className="space-y-4 animate-slide-in">
                <div className="w-20 h-20 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
                  <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
                </div>
                <div className="space-y-2">
                  <p className="text-lg font-medium text-slate-900">Envoi en cours...</p>
                  <Progress value={uploadProgress} className="w-64 mx-auto h-2" />
                  <p className="text-sm text-slate-500">{Math.round(uploadProgress)}%</p>
                </div>
              </div>
            ) : showSuccessAnimation ? (
              <div className="space-y-4 animate-success-pop">
                <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                </div>
                <p className="text-lg font-medium text-emerald-700">Documents envoyés !</p>
                <p className="text-sm text-slate-500">L'IA analyse vos fichiers...</p>
              </div>
            ) : isDragActive ? (
              <div className="space-y-4 animate-slide-in">
                <div className="w-20 h-20 mx-auto rounded-full bg-blue-100 flex items-center justify-center animate-pulse-soft">
                  <Upload className="h-10 w-10 text-blue-600" />
                </div>
                <p className="text-lg font-medium text-blue-700">Déposez vos fichiers ici</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                  <Upload className="h-10 w-10 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-medium text-slate-900">
                    Glissez vos factures ici
                  </p>
                  <p className="text-slate-500 mt-1">
                    ou cliquez pour sélectionner
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Badge variant="outline" className="bg-white">PDF</Badge>
                  <Badge variant="outline" className="bg-white">JPG</Badge>
                  <Badge variant="outline" className="bg-white">PNG</Badge>
                  <span className="text-slate-400">• max 10 MB</span>
                </div>
                <div className="flex items-center justify-center gap-2 pt-2">
                  <div className="flex items-center gap-1.5 text-sm text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full">
                    <Sparkles className="h-4 w-4" />
                    <span>Extraction automatique par IA</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          {[
            { value: 'all', label: 'Tous', count: stats.total },
            { value: 'pending', label: 'À traiter', count: stats.pending, color: 'amber' },
            { value: 'processed', label: 'Prêts', count: stats.processed, color: 'blue' },
            { value: 'error', label: 'Erreurs', count: stats.error, color: 'red' },
            { value: 'archived', label: 'Archivés', count: null }
          ].map((f) => (
            <Button
              key={f.value}
              variant={filter === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f.value)}
              className={`
                ${filter === f.value 
                  ? 'bg-slate-900 text-white' 
                  : 'bg-white hover:bg-slate-50'
                }
              `}
            >
              {f.label}
              {f.count !== null && f.count > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                  filter === f.value ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  {f.count}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Documents List */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b bg-slate-50/50">
            <CardTitle className="flex items-center justify-between text-lg">
              <span className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-slate-600" />
                Vos documents
              </span>
              <span className="text-sm font-normal text-slate-500">
                {items.length} document{items.length > 1 ? 's' : ''}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
                  <p className="text-slate-500">Chargement...</p>
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Inbox className="h-10 w-10 text-slate-400" />
                </div>
                <p className="text-lg font-medium text-slate-700">Aucun document</p>
                <p className="text-slate-500 mt-1 max-w-sm mx-auto">
                  Importez vos factures fournisseurs et l'IA les analysera automatiquement
                </p>
                <Button 
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                  onClick={() => document.querySelector('[data-testid="inbox-file-input"]')?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Importer un document
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {items.map((item, idx) => {
                  const statusConfig = getStatusConfig(item);
                  const StatusIcon = statusConfig.icon;
                  const amount = item.extracted_data?.amounts?.total_ttc;
                  const supplierName = item.extracted_data?.supplier?.name;
                  
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-4 p-4 md:p-5 hover:bg-slate-50 transition-colors cursor-pointer group animate-slide-in"
                      style={{ animationDelay: `${idx * 50}ms` }}
                      onClick={() => openDetailModal(item)}
                      data-testid={`inbox-item-${item.id}`}
                    >
                      {/* Status Icon */}
                      <div className={`
                        relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
                        ${statusConfig.bgClass}
                      `}>
                        <StatusIcon className={`h-6 w-6 ${statusConfig.textClass} ${statusConfig.animate ? 'animate-spin' : ''}`} />
                        {item.linked_invoice_id && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white">
                            <CheckCircle2 className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {supplierName || item.subject || 'Document sans titre'}
                            </p>
                            <p className="text-sm text-slate-500 mt-0.5">
                              {getDocTypeLabel(item.document_type)} • {formatDate(item.received_at)}
                            </p>
                          </div>
                          
                          {/* Amount */}
                          {amount && (
                            <div className="text-right flex-shrink-0">
                              <p className="text-lg font-bold text-slate-900">
                                {formatAmount(amount)}
                              </p>
                              {item.extracted_data?.amounts?.total_vat && (
                                <p className="text-xs text-slate-500">
                                  dont {formatAmount(item.extracted_data.amounts.total_vat)} TVA
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Status badge */}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge 
                            variant="outline" 
                            className={`text-xs bg-${statusConfig.color}-50 text-${statusConfig.color}-700 border-${statusConfig.color}-200`}
                          >
                            {statusConfig.label}
                          </Badge>
                          {item.linked_client_name && (
                            <Badge variant="outline" className="text-xs bg-slate-50">
                              <Building2 className="h-3 w-3 mr-1" />
                              {item.linked_client_name}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Arrow */}
                      <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedItem && (() => {
              const statusConfig = getStatusConfig(selectedItem);
              const StatusIcon = statusConfig.icon;
              
              return (
                <>
                  <DialogHeader className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${statusConfig.bgClass}`}>
                        <StatusIcon className={`h-6 w-6 ${statusConfig.textClass}`} />
                      </div>
                      <div>
                        <DialogTitle className="text-xl">
                          {selectedItem.extracted_data?.supplier?.name || selectedItem.subject || 'Document'}
                        </DialogTitle>
                        <DialogDescription className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={`bg-${statusConfig.color}-50 text-${statusConfig.color}-700`}>
                            {statusConfig.label}
                          </Badge>
                          <span className="text-slate-500">•</span>
                          <span>{statusConfig.description}</span>
                        </DialogDescription>
                      </div>
                    </div>
                  </DialogHeader>

                  <div className="space-y-6 py-4">
                    {/* Error */}
                    {selectedItem.error_message && (
                      <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-900">Problème détecté</p>
                          <p className="text-sm text-red-700 mt-1">{selectedItem.error_message}</p>
                        </div>
                      </div>
                    )}

                    {/* Montants */}
                    {selectedItem.extracted_data?.amounts && (
                      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200">
                        <div className="flex items-center gap-2 mb-4">
                          <Euro className="h-5 w-5 text-emerald-600" />
                          <h4 className="font-medium text-emerald-900">Montants détectés</h4>
                          {selectedItem.confidence && (
                            <Badge variant="outline" className="ml-auto text-xs bg-white">
                              <Star className="h-3 w-3 mr-1 text-amber-500" />
                              {Math.round(selectedItem.confidence * 100)}% confiance
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-center p-3 bg-white rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">Total HT</p>
                            <p className="text-lg font-bold text-slate-900">
                              {formatAmount(selectedItem.extracted_data.amounts.subtotal_ht) || '-'}
                            </p>
                          </div>
                          <div className="text-center p-3 bg-white rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">TVA</p>
                            <p className="text-lg font-bold text-slate-900">
                              {formatAmount(selectedItem.extracted_data.amounts.total_vat) || '-'}
                            </p>
                          </div>
                          <div className="text-center p-3 bg-emerald-100 rounded-lg">
                            <p className="text-xs text-emerald-600 mb-1">Total TTC</p>
                            <p className="text-2xl font-bold text-emerald-700">
                              {formatAmount(selectedItem.extracted_data.amounts.total_ttc) || '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Fournisseur */}
                    {selectedItem.extracted_data?.supplier && (
                      <div className="bg-slate-50 rounded-xl p-5 border">
                        <div className="flex items-center gap-2 mb-3">
                          <Building2 className="h-5 w-5 text-slate-600" />
                          <h4 className="font-medium text-slate-900">Fournisseur</h4>
                        </div>
                        <div className="space-y-2">
                          <p className="text-lg font-medium text-slate-900">
                            {selectedItem.extracted_data.supplier.name || 'Non identifié'}
                          </p>
                          {selectedItem.extracted_data.supplier.siren && (
                            <p className="text-sm text-slate-500">
                              SIREN : {selectedItem.extracted_data.supplier.siren}
                            </p>
                          )}
                          {selectedItem.extracted_data.supplier.address && (
                            <p className="text-sm text-slate-500">
                              {selectedItem.extracted_data.supplier.address}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Client lié */}
                    {selectedItem.linked_client_name && (
                      <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                        <CheckCircle2 className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-blue-900">Client associé</p>
                          <p className="text-sm text-blue-700">{selectedItem.linked_client_name}</p>
                        </div>
                      </div>
                    )}

                    {/* Facture créée */}
                    {selectedItem.linked_invoice_number && (
                      <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <FileCheck className="h-5 w-5 text-emerald-600" />
                        <div className="flex-1">
                          <p className="font-medium text-emerald-900">Facture créée</p>
                          <p className="text-sm text-emerald-700">{selectedItem.linked_invoice_number}</p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate('/dashboard/smartfacture')}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                        >
                          Voir <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    )}

                    <Separator />

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.status !== 'archived' && !selectedItem.linked_invoice_id && (
                        <>
                          <Button
                            onClick={() => handleCreateInvoice(selectedItem.id, selectedItem.linked_client_id)}
                            disabled={processing[`invoice_${selectedItem.id}`] || !selectedItem.extracted_data}
                            className="bg-emerald-600 hover:bg-emerald-700 flex-1 md:flex-none"
                          >
                            {processing[`invoice_${selectedItem.id}`] ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Zap className="h-4 w-4 mr-2" />
                            )}
                            Créer la facture
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => openLinkClientModal(selectedItem)}
                          >
                            <LinkIcon className="h-4 w-4 mr-2" />
                            Associer client
                          </Button>
                        </>
                      )}
                      {(selectedItem.status === 'error' || selectedItem.status === 'pending') && (
                        <Button
                          variant="outline"
                          onClick={() => handleReprocess(selectedItem.id)}
                          disabled={processing[selectedItem.id]}
                        >
                          {processing[selectedItem.id] ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                          )}
                          Relancer l'IA
                        </Button>
                      )}
                      {selectedItem.status !== 'archived' && (
                        <Button variant="outline" onClick={() => handleArchive(selectedItem.id)}>
                          <Archive className="h-4 w-4 mr-2" />
                          Archiver
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 hover:border-red-300"
                        onClick={() => handleDelete(selectedItem)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Link Client Modal */}
        <Dialog open={showLinkClientModal} onOpenChange={setShowLinkClientModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LinkIcon className="h-5 w-5 text-blue-600" />
                Associer à un client
              </DialogTitle>
              <DialogDescription>
                Liez ce document à un client existant pour un meilleur suivi
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Sélectionnez un client</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          {c.name}
                          {c.siren && <span className="text-slate-400 text-xs">({c.siren})</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Separator />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowLinkClientModal(false);
                  navigate('/dashboard/smartfacture/clients');
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer un nouveau client
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinkClientModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleLinkClient} disabled={!selectedClientId} className="bg-blue-600 hover:bg-blue-700">
                Associer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirmation de suppression */}
        <ConfirmDialog
          open={deleteConfirm.open}
          onOpenChange={(open) => setDeleteConfirm({ open, item: open ? deleteConfirm.item : null })}
          title="Supprimer ce document ?"
          description={`Supprimer "${deleteConfirm.item?.original_filename || 'ce document'}" ? Cette action est irréversible.`}
          confirmText="Supprimer"
          onConfirm={confirmDelete}
          loading={deleting}
          variant="destructive"
        />
      </div>
    </>
  );
};

export default InboxPage;
